import { PandaAvatar } from 'src/images';
import styles from './render-chat-messages.module.scss';
import React, { useMemo } from 'react';
import { MessageReferences } from './render-references';
import { ResponseActions } from './response-actions';
import { MessageResources } from './render-resources';
import { useSelector } from 'react-redux';
import { getUser } from 'shared/store/selectors/user.selector';
import { getKnowledgeItemById } from 'shared/store/selectors/knowledge.selector';
import ReactMarkdown from 'react-markdown';
import { getOwnerById } from 'shared/store/selectors/owners.selector';
import { useChatConversation } from '../../hooks/use-chat-conversation';
import { ANSWER_STATE } from 'shared/constants';

const C8_AI = 'C8 Panda';

function RenderMessage({ message, renderAvatar, ref }) {
  const knowledgeItemId = message?.references && message.references[0]?.knowledgeItemId;
  const knowledgeItem = useSelector(state => getKnowledgeItemById(state, knowledgeItemId));
  const { firstName = '', lastName = '' } = useSelector(({ owners }) =>
    getOwnerById(owners, { id: knowledgeItem?.ownerId }),
  );
  const { lang } = useChatConversation();

  const fullName = useMemo(() => `${firstName} ${lastName}`, [firstName, lastName]);

  const isResourcesOnly = message.state === ANSWER_STATE.ONLY_RESOURCES_FOUND;

  return (
    <div className={styles.message} key={message.id} ref={ref}>
      <div className={styles.avatar}>{renderAvatar(message.isUser)}</div>

      <div className={styles.content}>
        <h4>{message.isUser ? lang.USER_NAME : lang.BOT_NAME}</h4>
        <ReactMarkdown className={styles.messageContent}>{message.message}</ReactMarkdown>
        {!message.isUser && knowledgeItem && !isResourcesOnly && (
          <div className={styles.ownerSection}>
            <span>{`${lang.REASONING_KNOWLEDGE_ITEM}`}</span>
            <a href={`/knowledge/items/${knowledgeItem.id}`}>{knowledgeItem.title}</a>
            <span>{` ${lang.VETTED_BY}`}</span>
            <span className={styles.ownerFullName}>Dr. {fullName}.</span>
          </div>
        )}

        {!message.isUser && (
          <>
            <MessageReferences message={message} />

            <MessageResources
              message={message}
              excludeKnowledgeItemId={!isResourcesOnly ? knowledgeItemId : ''}
            />

            {!message.error && <ResponseActions message={message} />}
          </>
        )}
      </div>
    </div>
  );
}

export function RenderChatMessages() {
  const lastElementRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);
  const userProfile = useSelector(state => getUser(state));
  const { lang, clearChat, loading, messages } = useChatConversation();

  const shortName = React.useMemo(
    () =>
      userProfile?.firstName?.charAt(0)?.toUpperCase() +
      userProfile?.lastName?.charAt(0)?.toUpperCase(),
    [userProfile?.firstName, userProfile?.lastName],
  );

  const renderAvatar = React.useCallback(
    isUser => {
      if (isUser) {
        return <span className={styles.avatarName}>{shortName}</span>;
      }

      return <img src={PandaAvatar} alt={C8_AI} className={styles.panda} />;
    },
    [shortName],
  );

  const renderWelcomeMessage = React.useCallback(() => {
    return (
      <div className={styles.message}>
        <div className={styles.avatar}>{renderAvatar(false)}</div>

        <div className={styles.content}>
          <h4>{C8_AI}</h4>
          <p>{lang.WELCOME_MESSAGE.replace('{lastName}', userProfile?.lastName)}</p>
        </div>
      </div>
    );
  }, [lang.WELCOME_MESSAGE, renderAvatar, userProfile?.lastName]);

  const scrollToBottom = () => {
    scrollContainerRef.current.scrollTo({
      top: scrollContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div className={styles.root} ref={scrollContainerRef}>
      {messages.length > 0 && (
        <button type="button" className={styles.clearChat} onClick={clearChat}>
          {lang.CLEAR_CHAT}
        </button>
      )}
      {renderWelcomeMessage()}
      {messages.map((message, index) => (
        <RenderMessage
          message={message}
          key={message.id}
          renderAvatar={renderAvatar}
          ref={index === messages.length - 1 ? lastElementRef : null}
        />
      ))}

      {loading && (
        <div className={styles.message}>
          <div className={styles.avatar}>{renderAvatar(false)}</div>

          <div className={styles.content}>
            <h4>{lang.BOT_NAME}</h4>
            <div className={styles.loader}>
              <div className={styles.dot} />
              <div className={styles.dot} />
              <div className={styles.dot} />
              <div className={styles.dot} />
              <div className={styles.dot} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
